"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

/**
 * @module ol/style/Stroke
 */

/**
 * @typedef {Object} Options
 * @property {import("../color.js").Color|import("../colorlike.js").ColorLike} [color] A color, gradient or pattern.
 * See {@link module:ol/color~Color} and {@link module:ol/colorlike~ColorLike} for possible formats.
 * Default null; if null, the Canvas/renderer default black will be used.
 * @property {CanvasLineCap} [lineCap='round'] Line cap style: `butt`, `round`, or `square`.
 * @property {CanvasLineJoin} [lineJoin='round'] Line join style: `bevel`, `round`, or `miter`.
 * @property {Array<number>} [lineDash] Line dash pattern. Default is `null` (no dash).
 * Please note that Internet Explorer 10 and lower do not support the `setLineDash` method on
 * the `CanvasRenderingContext2D` and therefore this option will have no visual effect in these browsers.
 * @property {number} [lineDashOffset=0] Line dash offset.
 * @property {number} [miterLimit=10] Miter limit.
 * @property {number} [width] Width.
 */

/**
 * @classdesc
 * Set stroke style for vector features.
 * Note that the defaults given are the Canvas defaults, which will be used if
 * option is not defined. The `get` functions return whatever was entered in
 * the options; they will not return the default.
 * @api
 */
var Stroke =
/** @class */
function () {
  /**
   * @param {Options} [opt_options] Options.
   */
  function Stroke(opt_options) {
    var options = opt_options || {};
    /**
     * @private
     * @type {import("../color.js").Color|import("../colorlike.js").ColorLike}
     */

    this.color_ = options.color !== undefined ? options.color : null;
    /**
     * @private
     * @type {CanvasLineCap|undefined}
     */

    this.lineCap_ = options.lineCap;
    /**
     * @private
     * @type {Array<number>}
     */

    this.lineDash_ = options.lineDash !== undefined ? options.lineDash : null;
    /**
     * @private
     * @type {number|undefined}
     */

    this.lineDashOffset_ = options.lineDashOffset;
    /**
     * @private
     * @type {CanvasLineJoin|undefined}
     */

    this.lineJoin_ = options.lineJoin;
    /**
     * @private
     * @type {number|undefined}
     */

    this.miterLimit_ = options.miterLimit;
    /**
     * @private
     * @type {number|undefined}
     */

    this.width_ = options.width;
  }
  /**
   * Clones the style.
   * @return {Stroke} The cloned style.
   * @api
   */


  Stroke.prototype.clone = function () {
    var color = this.getColor();
    return new Stroke({
      color: Array.isArray(color) ? color.slice() : color || undefined,
      lineCap: this.getLineCap(),
      lineDash: this.getLineDash() ? this.getLineDash().slice() : undefined,
      lineDashOffset: this.getLineDashOffset(),
      lineJoin: this.getLineJoin(),
      miterLimit: this.getMiterLimit(),
      width: this.getWidth()
    });
  };
  /**
   * Get the stroke color.
   * @return {import("../color.js").Color|import("../colorlike.js").ColorLike} Color.
   * @api
   */


  Stroke.prototype.getColor = function () {
    return this.color_;
  };
  /**
   * Get the line cap type for the stroke.
   * @return {CanvasLineCap|undefined} Line cap.
   * @api
   */


  Stroke.prototype.getLineCap = function () {
    return this.lineCap_;
  };
  /**
   * Get the line dash style for the stroke.
   * @return {Array<number>} Line dash.
   * @api
   */


  Stroke.prototype.getLineDash = function () {
    return this.lineDash_;
  };
  /**
   * Get the line dash offset for the stroke.
   * @return {number|undefined} Line dash offset.
   * @api
   */


  Stroke.prototype.getLineDashOffset = function () {
    return this.lineDashOffset_;
  };
  /**
   * Get the line join type for the stroke.
   * @return {CanvasLineJoin|undefined} Line join.
   * @api
   */


  Stroke.prototype.getLineJoin = function () {
    return this.lineJoin_;
  };
  /**
   * Get the miter limit for the stroke.
   * @return {number|undefined} Miter limit.
   * @api
   */


  Stroke.prototype.getMiterLimit = function () {
    return this.miterLimit_;
  };
  /**
   * Get the stroke width.
   * @return {number|undefined} Width.
   * @api
   */


  Stroke.prototype.getWidth = function () {
    return this.width_;
  };
  /**
   * Set the color.
   *
   * @param {import("../color.js").Color|import("../colorlike.js").ColorLike} color Color.
   * @api
   */


  Stroke.prototype.setColor = function (color) {
    this.color_ = color;
  };
  /**
   * Set the line cap.
   *
   * @param {CanvasLineCap|undefined} lineCap Line cap.
   * @api
   */


  Stroke.prototype.setLineCap = function (lineCap) {
    this.lineCap_ = lineCap;
  };
  /**
   * Set the line dash.
   *
   * Please note that Internet Explorer 10 and lower [do not support][mdn] the
   * `setLineDash` method on the `CanvasRenderingContext2D` and therefore this
   * property will have no visual effect in these browsers.
   *
   * [mdn]: https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/setLineDash#Browser_compatibility
   *
   * @param {Array<number>} lineDash Line dash.
   * @api
   */


  Stroke.prototype.setLineDash = function (lineDash) {
    this.lineDash_ = lineDash;
  };
  /**
   * Set the line dash offset.
   *
   * @param {number|undefined} lineDashOffset Line dash offset.
   * @api
   */


  Stroke.prototype.setLineDashOffset = function (lineDashOffset) {
    this.lineDashOffset_ = lineDashOffset;
  };
  /**
   * Set the line join.
   *
   * @param {CanvasLineJoin|undefined} lineJoin Line join.
   * @api
   */


  Stroke.prototype.setLineJoin = function (lineJoin) {
    this.lineJoin_ = lineJoin;
  };
  /**
   * Set the miter limit.
   *
   * @param {number|undefined} miterLimit Miter limit.
   * @api
   */


  Stroke.prototype.setMiterLimit = function (miterLimit) {
    this.miterLimit_ = miterLimit;
  };
  /**
   * Set the width.
   *
   * @param {number|undefined} width Width.
   * @api
   */


  Stroke.prototype.setWidth = function (width) {
    this.width_ = width;
  };

  return Stroke;
}();

var _default = Stroke;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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